/**
 * ThreadTypeGlyph — the ONE thread-type marker (sidebar/header UX round 3,
 * 2026-08-01). Sibling of RoutineGlyph, same conventions: a compact inline-SVG
 * icon rendered on the LEFT of a thread row/chip telling the user what KIND of
 * thread it is:
 *   • kind="group"      → two-people glyph (a thread with ≥2 participants —
 *     the SAME predicate as the header's participant line, see
 *     `isGroupParticipantCount` in services/groupThreads/senderIdentity);
 *   • kind="individual" → chat-bubble glyph (a thread only this user is in);
 *   • kind="direct"     → one-person glyph (PR-5, 2026-09-09: a DIRECT
 *     MESSAGE — exactly two humans, the viewer one of them; the row is
 *     named after the other person, see threadDisplayTitle).
 *
 * Routines v2 (2026-08-16): routine rows that are GROUP threads wear this
 * glyph BESIDE RoutineGlyph (the "group icon also indicated" directive) —
 * those call sites pass `data-routine-group-glyph=""` as the stable hook for
 * the paired marker, so extra props are spread onto the <svg>.
 *
 * Coloring mirrors RoutineGlyph's active/muted convention on the dark rail:
 * active (selected) rows wear the family's brighter gold, inactive rows the
 * muted gold the row meta uses. Pure presentational; no state, no effects.
 * `data-thread-type-glyph` (valued "group"/"individual"/"direct") is the
 * stable test hook; aria-labels are the plain words "Group thread" /
 * "Thread" / "Direct message".
 */
import React from 'react';

export const THREAD_TYPE_GLYPH_ACTIVE_COLOR = 'var(--brand-gold)';
export const THREAD_TYPE_GLYPH_MUTED_COLOR = 'rgba(182, 146, 76, 0.55)';

export interface ThreadTypeGlyphProps extends React.SVGAttributes<SVGSVGElement> {
  /** Which type marker to draw. */
  kind: 'group' | 'individual' | 'direct';
  /** True → brighter gold (active row); false (default) → muted gold. */
  active?: boolean;
  /** Square icon size in px (default 13 — sized for row/chip meta lines). */
  size?: number;
}

export const ThreadTypeGlyph: React.FC<ThreadTypeGlyphProps> = ({ kind, active = false, size = 13, ...rest }) => (
  <svg
    {...rest}
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke={active ? THREAD_TYPE_GLYPH_ACTIVE_COLOR : THREAD_TYPE_GLYPH_MUTED_COLOR}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label={kind === 'group' ? 'Group thread' : kind === 'direct' ? 'Direct message' : 'Thread'}
    data-thread-type-glyph={kind}
    data-thread-type-active={active ? 'true' : 'false'}
    style={{ flex: 'none', verticalAlign: 'middle' }}
  >
    {kind === 'group' ? (
      <>
        {/* Two people (mirrors the header's AddPeopleSvgIcon body, sans plus). */}
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ) : kind === 'direct' ? (
      <>
        {/* One person (the two-people glyph's left figure, alone). */}
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ) : (
      /* Chat bubble. */
      <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 1 1 16.1-3.8z" />
    )}
  </svg>
);

export default ThreadTypeGlyph;

/**
 * Section marker for a PERSONAL CATEGORY (PR-5, 2026-09-09): a folder, in the
 * same active/muted gold convention. Sibling of ThreadTypeGlyph — a category is
 * a place the viewer files threads, not a thread type, so it is its own glyph.
 */
export const CategoryFolderGlyph: React.FC<Omit<ThreadTypeGlyphProps, 'kind'>> = ({ active = false, size = 13, stroke, ...rest }) => (
  <svg
    {...rest}
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke={stroke ?? (active ? THREAD_TYPE_GLYPH_ACTIVE_COLOR : THREAD_TYPE_GLYPH_MUTED_COLOR)}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label="Category"
    data-category-glyph=""
    style={{ flex: 'none', verticalAlign: 'middle' }}
  >
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);
